import type {
  PermissionCatalog,
  PermissionCatalogEntry,
  PermissionOverrideInput,
  PermissionSlug,
  UserPermissionsView,
} from '../../services/userPermissionsService';

/**
 * The permission editor's RULES, as pure functions over `(view, catalog, state)`
 * (design §C). No React, no MUI, no network — `UserPermissionsDialog.tsx` is a
 * thin shell over this, which is what makes the rules testable at all.
 *
 * THREE THINGS A READER MUST TAKE FROM HERE:
 *
 * 1. BASELINE = `view.effective`, the server's own `before`. Every switch can be
 *    returned to the value it loaded with, so an actor who lacks slug X can turn X
 *    off on a target who holds it and then change their mind — the server accepts
 *    the restored state as a gain-free no-op, and a UI that could not undo it would
 *    trap the admin with no way out but Cancel.
 *
 * 2. `toOverrides` is STATE-based, never a diff of clicks. `PUT /users/:id/permissions`
 *    is a FULL REPLACEMENT (dto/user-permissions.dto.ts:24-28), so a body built from
 *    the switch that was just clicked silently clears every other customization.
 *
 * 3. Every rule here is an OPTIMISTIC HINT, never the authority. The live actor's
 *    permissions are re-resolved under the clinic row lock server-side
 *    (permission-integrity.ts:98-125), and the ≥1-active-holder floor needs every
 *    user's effective set, which this screen does not have. A 403 or 400 the UI did
 *    not predict must still render the server's own sentence.
 */

export interface EditorRow {
  slug: PermissionSlug;
  /** The pending value. Seeded from `view.effective`. */
  on: boolean;
  roleDefault: boolean;
  /** The LOADED customization state (a saved divergence), not the pending one. */
  customized: boolean;
  editable: boolean;
  lockReason: string | null;
  changedBy: string | null;
  changedAt: string | null;
}

export interface EditorState {
  /** Keyed by slug, INSERTED in catalog order — every derived list inherits that order. */
  rows: Record<string, EditorRow>;
  /** Reset was pressed: the save also clears the not-enforced rows it otherwise keeps. */
  resetRequested: boolean;
}

export interface EditorContext {
  /** The signed-in actor's effective set, from `GET /users/me`. A 30 s-TTL snapshot. */
  actorPermissions: readonly string[];
}

export const CEILING_BLOCK_REASON =
  'This would give them an ability you do not have yourself. Only someone who holds it can grant it.';

export const OWNER_LOCK_REASON =
  'The clinic Owner always keeps this. Change their role first if that is what you meant.';

/**
 * Why `toggle` refuses a row the screen does not show (#85, supersedes #68).
 *
 * ⚠ IT IS NOT COPY, and a clinic never reads it. It replaces `NOT_ENFORCED_REASON`, which WAS
 * copy: it re-exported the deck's "Not enforced yet" sentence so the read-only row and its
 * caption were one spelling. There is no such row any more — `visibleCategories` drops it — so
 * the deck's sentence is deleted and this one stays inside the model, as the honest answer for a
 * caller that reaches a slug by name rather than through the screen.
 *
 * It is held to the no-long-dash rule anyway (`permissionNotes.test.ts` walks it): the one way
 * it could ever reach a reader is a future change that un-hides these rows, and that change
 * should not have to remember.
 */
export const NOT_SHOWN_REASON =
  'This ability is not shown on this screen, because it does not control anything yet.';

export const UNKNOWN_SLUG_REASON =
  'This ability is not in the list this screen loaded. Reload and try again.';

/**
 * The first word of a display name — not a rule, but the one SPELLING of it.
 *
 * The iPhone gate's caution and its tick address the person, not the row, and the
 * SAME rendered string is built on two surfaces: the editor's footer and the Team
 * screen's role-change confirm. Spelled twice, they could show one clinic two
 * different names for one person while asking the same question. It lives here
 * rather than beside either renderer because this module is what both already
 * import — `UserManagement.tsx` imports the editor, never the other way round
 * (`teamTypes.ts` exists to keep that direction one-way).
 *
 * `name` is free text, so a single-word or whitespace-only name degrades to the
 * whole string rather than to ''.
 */
export const firstNameOf = (name: string): string => name.trim().split(/\s+/)[0] || name;

function entries(catalog: PermissionCatalog): PermissionCatalogEntry[] {
  return catalog.categories.flatMap((c) => c.permissions);
}

/**
 * WHAT THE SCREEN SHOWS (#85, 2026-09-21, supersedes #68).
 *
 * A slug the catalog marks `notEnforced` gates nothing, so a switch for it is a control a clinic
 * has to read and cannot use. #68 shipped those four as read-only rows carrying a sentence that
 * said so, on the reasoning that hiding something the API still accepts is its own dishonesty.
 * That is an argument about the API, and the person in front of the screen is not having it: the
 * honest fix is to stop offering the control, not to write a better caption under a dead one.
 *
 * A CATEGORY LEFT WITH NO VISIBLE ROW IS DROPPED with them. Today that removes the whole `System`
 * heading, which carries `manage:subscription` alone — an empty accordion is the same dead
 * control one level up.
 *
 * ⚠ DRIVEN BY THE FLAG, NEVER BY A LIST OF NAMES. `NOT_ENFORCED_PERMISSIONS` went stale twice
 * during this program (`manage:roles_permissions` at P4, `view:discharge_summaries` at P5), which
 * is why the backend DERIVES the flag; a second list on the web would go stale the same way and
 * would hide a live revoke. The day a slug gains a gate and leaves that array, its row comes back
 * here by itself with no edit.
 *
 * ⚠ THIS IS A VIEW, NOT A FILTER ON THE SAVE. `buildEditorState`, `toOverrides`, `applyReset` and
 * `isDirty` all still walk the WHOLE catalog, because the PUT is a full replacement: a save that
 * stopped sending a hidden slug's stored row would DELETE the clinic's decision behind a control
 * that had just disappeared. `toOverrides` passes it through untouched instead.
 *
 * A FRESH object per call, so nothing a caller does to the result reaches the cached catalog.
 */
export function visibleCategories(
  catalog: PermissionCatalog,
): PermissionCatalog['categories'] {
  return catalog.categories
    .map((c) => ({ ...c, permissions: c.permissions.filter((p) => !p.notEnforced) }))
    .filter((c) => c.permissions.length > 0);
}

/** The visible rows, flattened — catalog order, the same order every derived list inherits. */
export function visibleEntries(catalog: PermissionCatalog): PermissionCatalogEntry[] {
  return visibleCategories(catalog).flatMap((c) => c.permissions);
}

function entryOf(
  catalog: PermissionCatalog,
  slug: PermissionSlug,
): PermissionCatalogEntry | undefined {
  return entries(catalog).find((e) => e.slug === slug);
}

/**
 * THE "customized" predicate — the ONE used by the row chip, the category chip, the
 * dialog chip, the count and the role-change confirm.
 *
 * Membership in `overrides` is deliberately NOT the answer: PUT happily accepts a
 * row whose `enabled` equals the role default, so such rows exist, and `toOverrides`
 * drops them on the next save. Calling one "Customized" would make the chip vanish
 * for a permission the admin never touched.
 */
export function isCustomized(view: UserPermissionsView, slug: PermissionSlug): boolean {
  const row = view.overrides.find((o) => o.permission === slug);
  return row !== undefined && row.enabled !== view.defaults.includes(slug);
}

/**
 * EVERY customization the server holds, hidden rows included.
 *
 * This is the number for a question about what a WRITE will do: the role-change confirm ("n
 * customized permissions will be removed" — `users.service.ts:360-372` deletes them all) and the
 * Reset confirm ("n will be cleared" — `resetRequested` drops the not-enforced rows too). Both
 * would under-report if they counted only what is on screen, which is the opposite failure from
 * the badge below.
 */
export function customizedCount(view: UserPermissionsView): number {
  return view.overrides.filter((o) => isCustomized(view, o.permission)).length;
}

/**
 * The customizations THIS SCREEN SHOWS — the number behind the dialog's `Customized` badge (#85).
 *
 * A badge is a pointer: it promises a row the reader can go and find. Counting a hidden slug
 * would put the badge on a dialog where no category chip and no row caption agrees with it, and
 * the reader would open all ten headings looking for the one that is not there. So the badge
 * counts visible rows, and the two CONFIRMS keep `customizedCount` above, because they are about
 * rows the save really does move.
 */
export function visibleCustomizedCount(
  view: UserPermissionsView,
  catalog: PermissionCatalog,
): number {
  return visibleEntries(catalog).filter((entry) => isCustomized(view, entry.slug)).length;
}

export function buildEditorState(
  view: UserPermissionsView,
  catalog: PermissionCatalog,
): EditorState {
  const rows: Record<string, EditorRow> = {};
  entries(catalog).forEach((entry) => {
    const stored = view.overrides.find((o) => o.permission === entry.slug);
    // The Owner lock is applied at RESOLUTION for every OWNER target
    // (permissions.service.ts:101), so the switch is locked ON rather than offered
    // and refused with a 400. The slug is never spelled here — the catalog says which.
    const ownerLocked = view.role === 'OWNER' && entry.ownerLocked;
    rows[entry.slug] = {
      slug: entry.slug,
      on: view.effective.includes(entry.slug),
      roleDefault: view.defaults.includes(entry.slug),
      customized: isCustomized(view, entry.slug),
      editable: !ownerLocked && !entry.notEnforced,
      // ⚠ A row is still BUILT for a hidden slug, and still `editable: false`. The state is what
      // `toOverrides`, `applyReset` and `isDirty` walk, and dropping the row here would drop the
      // stored override with it. `NOT_SHOWN_REASON` never reaches a reader: the row is not
      // rendered, so only a caller that names the slug directly can see it.
      lockReason: ownerLocked ? OWNER_LOCK_REASON : entry.notEnforced ? NOT_SHOWN_REASON : null,
      changedBy: stored ? stored.updatedBy : null,
      changedAt: stored ? stored.updatedAt : null,
    };
  });
  return { rows, resetRequested: false };
}

/**
 * The COMPLETE desired override set, in catalog order.
 *
 * For every EDITABLE slug: an entry iff the desired value differs from the role
 * default. For every NOT-ENFORCED slug: the EXISTING row passed through untouched —
 * unless Reset was pressed, whose promise is "role defaults" and which therefore
 * clears those too (the confirm says so).
 *
 * ⚠ THE PASS-THROUGH IS LOAD-BEARING, AND MORE SO SINCE #85 HID THOSE ROWS. `PUT
 * /users/:id/permissions` is a FULL REPLACEMENT, so omitting a hidden slug's stored
 * row DELETES it. While the row was on screen that would at least have been visible;
 * now it would be a save silently discarding a clinic's stored decision about a
 * control the screen no longer shows — with nothing on screen looking wrong either
 * before or after. This walk is over the WHOLE catalog for exactly that reason, and
 * `permissionEditorModel.test.ts` pins the hidden-slug case by itself.
 */
export function toOverrides(
  state: EditorState,
  view: UserPermissionsView,
  catalog: PermissionCatalog,
): PermissionOverrideInput[] {
  const out: PermissionOverrideInput[] = [];
  entries(catalog).forEach((entry) => {
    if (entry.notEnforced) {
      if (state.resetRequested) return;
      const stored = view.overrides.find((o) => o.permission === entry.slug);
      if (stored) out.push({ permission: entry.slug, enabled: stored.enabled });
      return;
    }
    const row = state.rows[entry.slug];
    if (!row) return;
    if (row.on !== view.defaults.includes(entry.slug)) {
      out.push({ permission: entry.slug, enabled: row.on });
    }
  });
  return out;
}

/**
 * The stored rows the NEXT SAVE TIDIES AWAY: a row whose `enabled` already equals
 * the role default, so `toOverrides` drops it and the server deletes it.
 *
 * The dialog's caption is derived from THIS and never from bare `isDirty(pristine)`,
 * which is true for three different states and the sentence is true of only one:
 *  - a row the catalog no longer carries — dropped silently, because this walk and
 *    `toOverrides` both walk the CATALOG. Nothing about it "matches the role
 *    default", so it is excluded here;
 *  - a legacy revoke stored against the OWNER-LOCKED slug on an OWNER target — the
 *    resolver re-adds it (permissions.service.ts:101) so the save clears the row,
 *    but the row it clears CONTRADICTS the default; excluded by the `enabled ===`
 *    test itself (the slug is never spelled here — the catalog says which);
 *  - and the one the sentence is about.
 *
 * NOT-ENFORCED rows are skipped for a different reason: a normal save passes their
 * stored row THROUGH untouched, so promising to tidy one up would be a promise the
 * save does not keep. (Reset does clear them — and says so in its own confirm.)
 */
export function redundantOverrideSlugs(
  view: UserPermissionsView,
  catalog: PermissionCatalog,
): PermissionSlug[] {
  return entries(catalog)
    .filter((entry) => !entry.notEnforced)
    .filter((entry) => {
      const stored = view.overrides.find((o) => o.permission === entry.slug);
      return stored !== undefined && stored.enabled === view.defaults.includes(entry.slug);
    })
    .map((entry) => entry.slug);
}

function asMap(rows: readonly PermissionOverrideInput[]): Map<string, boolean> {
  return new Map(rows.map((r) => [r.permission, r.enabled]));
}

/** Dirty = the set this form would POST differs from the set the server holds. */
export function isDirty(
  state: EditorState,
  view: UserPermissionsView,
  catalog: PermissionCatalog,
): boolean {
  const next = asMap(toOverrides(state, view, catalog));
  const now = asMap(view.overrides.map((o) => ({ permission: o.permission, enabled: o.enabled })));
  if (next.size !== now.size) return true;
  let same = true;
  next.forEach((enabled, slug) => {
    if (now.get(slug) !== enabled) same = false;
  });
  return !same;
}

/**
 * THE GRANT CEILING, measured the way the server measures it: a slug that would end
 * up ON, is not already effective on the target, and is not held by the live actor.
 * Turning something OFF is never refused, so the DIRECTION is the caller's to
 * decide — this answers only "would granting this be a gain the actor cannot make".
 *
 * Exported because two places need the same answer: `toggle` below, over the whole
 * coupled set, and the dialog's per-row switch, which must be dead exactly when
 * `toggle` would refuse it. A second spelling in the shell would agree today and
 * drift later, with this file's unit suite still green.
 *
 * A HINT, never the authority: the server re-resolves the actor under the clinic row
 * lock (permission-integrity.ts:98-125) and can still refuse what this allowed.
 */
export function ceilingBlocks(
  view: UserPermissionsView,
  slug: PermissionSlug,
  ctx: EditorContext,
): boolean {
  return !view.effective.includes(slug) && !ctx.actorPermissions.includes(slug);
}

export type ToggleResult =
  | { ok: true; state: EditorState; alsoChanged: PermissionSlug[] }
  | { ok: false; reason: string };

/**
 * Flip one switch, with everything decision #50 says must move with it.
 *
 * COUPLING: turning X OFF turns OFF every EDITABLE slug whose `requiresView` is X;
 * turning a slug ON turns ON the slug it requires. Both are computed to a FIXPOINT
 * over the whole catalog, so a future chained edge (A requires B requires C) moves
 * the whole chain — every edge in the shipped map is one hop today.
 *
 * ⚠ COUPLING SKIPS NON-EDITABLE SLUGS. A slug that enforces nothing cannot make a
 * configuration dishonest, so leaving its row alone is correct — and coupling it
 * would make `view:inventory` and `view:discharge_summaries` permanently
 * un-turn-off-able, since only not-enforced slugs require them on some paths.
 * It also means a coupled change can never hit the Owner lock (an Owner-locked row
 * is not editable), so that refusal below is always about the slug the user clicked.
 *
 * REFUSALS are all-or-nothing: nothing is half-applied, because a switch that moved
 * beside an error the admin cannot act on is the control-that-lies INV-106 bans.
 */
export function toggle(
  state: EditorState,
  view: UserPermissionsView,
  catalog: PermissionCatalog,
  slug: PermissionSlug,
  on: boolean,
  ctx: EditorContext,
): ToggleResult {
  const all = entries(catalog);
  const entry = entryOf(catalog, slug);
  const row = state.rows[slug];
  if (!entry || !row) return { ok: false, reason: UNKNOWN_SLUG_REASON };
  if (!row.editable) return { ok: false, reason: row.lockReason ?? UNKNOWN_SLUG_REASON };

  const desired = new Map<PermissionSlug, boolean>([[slug, on]]);
  const editable = (s: PermissionSlug): boolean => state.rows[s]?.editable === true;

  // Fixpoint: keep applying the two coupling rules until nothing new moves.
  let grew = true;
  while (grew) {
    grew = false;
    all.forEach((e) => {
      const want = desired.get(e.slug);
      // ON pulls its requirement ON.
      if (want === true && e.requiresView && editable(e.requiresView)) {
        if (desired.get(e.requiresView) !== true) {
          desired.set(e.requiresView, true);
          grew = true;
        }
      }
      // OFF pushes every dependant OFF.
      if (e.requiresView && desired.get(e.requiresView) === false && editable(e.slug)) {
        if (desired.get(e.slug) !== false) {
          desired.set(e.slug, false);
          grew = true;
        }
      }
    });
  }

  // THE CEILING, over everything this toggle would turn ON — the clicked slug and
  // whatever coupling pulled with it. `ceilingBlocks` above is the rule, shared with
  // the dialog's per-row switch so the two can never disagree.
  // A plain loop, not `.forEach`: the refusal must short-circuit the whole toggle.
  const wanted = Array.from(desired.entries());
  for (let i = 0; i < wanted.length; i += 1) {
    const [s, value] = wanted[i];
    if (!value) continue;
    if (ceilingBlocks(view, s, ctx)) {
      return { ok: false, reason: CEILING_BLOCK_REASON };
    }
  }

  const rows: Record<string, EditorRow> = { ...state.rows };
  const alsoChanged: PermissionSlug[] = [];
  all.forEach((e) => {
    const value = desired.get(e.slug);
    if (value === undefined) return;
    const current = rows[e.slug];
    if (!current || current.on === value) return;
    rows[e.slug] = { ...current, on: value };
    if (e.slug !== slug) alsoChanged.push(e.slug);
  });

  return { ok: true, state: { ...state, rows }, alsoChanged };
}

/**
 * "Reset to role defaults" as a LOCAL action, not a second mutation.
 *
 * EVERY row the save can move goes back to its role default and `resetRequested` is
 * set, so the ONE save also clears the stored not-enforced rows a normal save passes
 * through — which is what `DELETE /users/:id/permissions` (the PUT path with `[]`)
 * really does, and what the confirm promises. Cancel still undoes all of it,
 * because nothing has been sent.
 *
 * ⚠ THE NOT-ENFORCED ROWS MOVE TOO, even though they are not editable — and since
 * #85 they are not rendered either, so the reason has changed and the behaviour has
 * not. It USED to be about the screen: `toOverrides` DROPS their stored rows under
 * `resetRequested`, so leaving a visible disabled switch where it loaded would have
 * shown one value while the pending save posted another, which is the
 * control-that-lies INV-106 bans. With the row hidden there is no such control, and
 * what remains is the same property stated over the STATE: after a Reset every row in
 * `state.rows` holds the value this save produces, hidden rows included, so nothing
 * downstream (`isDirty`, a future re-render, a slug that stops being inert while the
 * dialog is open) can read a position the save contradicts. That invariant is what
 * `permissionEditorModel.test.ts` welds row by row, and it is cheaper to keep than to
 * re-derive the day one of these rows comes back.
 *
 * The ONE row left alone is an Owner-locked one: resolution re-adds it for every
 * OWNER target (permissions.service.ts:101) whatever the body says, so sending it to
 * a default that may not carry it would be the same lie in the other direction.
 */
export function applyReset(
  state: EditorState,
  view: UserPermissionsView,
  catalog: PermissionCatalog,
): EditorState {
  const rows: Record<string, EditorRow> = { ...state.rows };
  entries(catalog).forEach((entry) => {
    const current = rows[entry.slug];
    if (!current) return;
    if (view.role === 'OWNER' && entry.ownerLocked) return;
    rows[entry.slug] = { ...current, on: view.defaults.includes(entry.slug) };
  });
  return { rows, resetRequested: true };
}

/**
 * Why the Reset button is disabled, or null.
 *
 * Reset is a SECOND ceiling path and the one people miss: clearing a stored
 * `enabled: false` row RESTORES a role default, and a default the target does not
 * currently hold is a GAIN. If the actor does not hold it either, the server refuses
 * the whole reset (permission-integrity.ts:127-140, reached identically from the
 * DELETE path). Measured over EVERY default, not just the editable ones — Reset
 * clears the not-enforced rows too, so their defaults come back as well.
 */
export function resetBlockedReason(
  view: UserPermissionsView,
  catalog: PermissionCatalog,
  ctx: EditorContext,
): string | null {
  const known = new Set(entries(catalog).map((e) => e.slug));
  const wouldGain = view.defaults.filter(
    (slug) => known.has(slug) && !view.effective.includes(slug),
  );
  const missing = wouldGain.find((slug) => !ctx.actorPermissions.includes(slug));
  return missing ? CEILING_BLOCK_REASON : null;
}

/** Effective before, OFF after — in catalog order (the row insertion order). */
export function narrowedSlugs(
  state: EditorState,
  view: UserPermissionsView,
): PermissionSlug[] {
  return Object.values(state.rows)
    .filter((row) => !row.on && view.effective.includes(row.slug))
    .map((row) => row.slug);
}

/**
 * The narrowing the iPhone gate is about: the slugs the offline plane actually
 * reads. Narrowing anything else cannot strand a queued operation, so it is not
 * gated (design §D, #66).
 */
export function syncNarrowedSlugs(
  state: EditorState,
  view: UserPermissionsView,
  catalog: PermissionCatalog,
): PermissionSlug[] {
  const sync = new Set(entries(catalog).filter((e) => e.affectsOfflineSync).map((e) => e.slug));
  return narrowedSlugs(state, view).filter((slug) => sync.has(slug));
}

/**
 * The same question for a ROLE change, which clears every override in the same
 * transaction (users.service.ts:360-372) and so re-bases the user on the new role's
 * defaults. Catalog-ordered; a slug the catalog does not carry is inert and omitted.
 */
export function roleChangeNarrowing(
  effective: readonly PermissionSlug[],
  newRoleDefaults: readonly PermissionSlug[],
  catalog: PermissionCatalog,
): { narrowed: PermissionSlug[]; syncNarrowed: PermissionSlug[] } {
  const all = entries(catalog);
  const narrowed = all
    .filter((e) => effective.includes(e.slug) && !newRoleDefaults.includes(e.slug))
    .map((e) => e.slug);
  const sync = new Set(all.filter((e) => e.affectsOfflineSync).map((e) => e.slug));
  return { narrowed, syncNarrowed: narrowed.filter((slug) => sync.has(slug)) };
}
